import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChevronLeft, Pencil, Archive, Paperclip } from "lucide-react";
import { api, errMsg, eur, fdate, fileUrl, CATEGORIES, INVOICE_STATUS, ORDER_STATUS, ASSET_STATUS, can } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge, DataTable, Spinner, uploadFile } from "@/components/common";
import Modal from "@/components/Modal";
import { SupplierForm } from "@/pages/Suppliers";

const TABS = [["invoices", "Factures"], ["orders", "Commandes"], ["contracts", "Contrats"], ["assets", "Matériel"]];

export function Attachments({ entity, id, items = [], onChange, editable }) {
  const add = async (file) => {
    try {
      const m = await uploadFile(file);
      await api.post(`/attachments/${entity}/${id}`, { file_id: m.id, name: m.name });
      toast.success("Pièce jointe ajoutée");
      onChange();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="attachments">
      <div className="mb-2 flex items-center justify-between"><h3 className="text-sm font-bold uppercase tracking-wide text-[#6B6B80]">Pièces jointes</h3>
        {editable && <label className="btn-ghost cursor-pointer"><Paperclip size={14} /> Ajouter<input type="file" hidden onChange={(e) => e.target.files[0] && add(e.target.files[0])} data-testid="attachment-input" /></label>}</div>
      {items.length ? items.map((a, i) => <a key={i} href={fileUrl(a.file_id)} target="_blank" rel="noreferrer" className="block truncate text-sm font-semibold underline decoration-[#A5894E] underline-offset-4">{a.name}</a>) : <p className="text-xs text-[#6B6B80]">Aucune pièce jointe.</p>}
    </div>
  );
}

export default function SupplierDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [tab, setTab] = useState("invoices");
  const [edit, setEdit] = useState(false);
  const load = () => api.get(`/suppliers/${id}`).then((r) => setS(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  if (!s) return <Spinner />;
  const editable = can(user, "createur");
  const tables = {
    invoices: [[{ key: "number", label: "N°" }, { key: "date", label: "Date", type: "date" }, { key: "status", label: "Statut", value: (r) => INVOICE_STATUS[r.status][0], render: (r) => <Badge meta={INVOICE_STATUS[r.status]} /> }, { key: "total_ttc", label: "TTC", type: "money" }], s.invoices, (r) => nav(`/factures/${r.id}`)],
    orders: [[{ key: "number", label: "N°" }, { key: "date", label: "Date", type: "date" }, { key: "status", label: "Statut", value: (r) => ORDER_STATUS[r.status][0], render: (r) => <Badge meta={ORDER_STATUS[r.status]} /> }, { key: "total_ttc", label: "TTC", type: "money" }], s.orders],
    contracts: [[{ key: "reference", label: "Référence" }, { key: "subject", label: "Objet" }, { key: "end_date", label: "Fin", type: "date" }, { key: "annual_amount", label: "Montant annuel", type: "money" }], s.contracts],
    assets: [[{ key: "inventory_number", label: "N° inventaire" }, { key: "type", label: "Type" }, { key: "model", label: "Modèle" }, { key: "status", label: "Statut", value: (r) => ASSET_STATUS[r.status][0] }, { key: "price_ht", label: "Prix HT", type: "money" }], s.assets],
  };
  const [cols, rows, click] = tables[tab];
  const archive = async () => { if (!window.confirm("Archiver ce fournisseur ?")) return; await api.delete(`/suppliers/${id}`); toast.success("Fournisseur archivé"); nav("/fournisseurs"); };
  return (
    <div>
      <button className="btn-ghost mb-4" onClick={() => nav("/fournisseurs")} data-testid="supplier-back-btn"><ChevronLeft size={16} /> Fournisseurs</button>
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight" data-testid="supplier-name">{s.name}</h1>
          <div className="mt-2 h-0.5 w-10 bg-[#A5894E]" />
          <p className="mt-3 text-sm text-[#6B6B80]">{CATEGORIES[s.category]} · compte <span className="mono">{s.account}</span> · {s.active ? "actif" : "inactif"}</p>
        </div>
        {editable && <div className="flex gap-2"><button className="btn-secondary" onClick={() => setEdit(true)} data-testid="edit-supplier-btn"><Pencil size={15} /> Modifier</button><button className="btn-ghost" onClick={archive} data-testid="archive-supplier-btn"><Archive size={15} /> Archiver</button></div>}
      </div>
      <div className="mb-8 grid gap-6 lg:grid-cols-3">
        <div className="card space-y-2 p-5 text-sm">
          {[["SIRET", s.siret], ["TVA intracom.", s.vat_number], ["Adresse", s.address], ["Contact", s.contact_name], ["Email", s.contact_email], ["Téléphone", s.contact_phone], ["Conditions", s.payment_terms]].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 border-b border-[#E4E4EC] pb-2 last:border-0"><span className="text-[#6B6B80]">{k}</span><span className="text-right font-semibold">{v || "—"}</span></div>
          ))}
          {s.notes && <p className="pt-2 text-xs text-[#6B6B80]">{s.notes}</p>}
        </div>
        <div className="card p-5" data-testid="supplier-spent-chart">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-[#6B6B80]">Total dépensé par année (TTC)</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={s.spent_by_year}><XAxis dataKey="year" tick={{ fontSize: 11 }} /><YAxis hide /><Tooltip formatter={(v) => eur(v)} /><Bar dataKey="total" fill="#A5894E" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
          {s.spent_by_year.map((y) => <p key={y.year} className="num text-sm"><b>{y.year}</b> : {eur(y.total)}</p>)}
        </div>
        <div className="card p-5"><Attachments entity="fournisseur" id={id} items={s.attachments} onChange={load} editable={editable} /></div>
      </div>
      <div className="mb-4 flex flex-wrap gap-1 border-b border-[#E4E4EC]">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`supplier-tab-${k}`} className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${tab === k ? "border-[#A5894E] text-[#16163F]" : "border-transparent text-[#6B6B80]"}`}>{l} ({tables[k][1].length})</button>)}
      </div>
      <DataTable columns={cols} rows={rows} onRowClick={click} title={`${s.name} ${tab}`} testid={`supplier-${tab}-table`} pageSize={10} />
      <Modal open={edit} onClose={() => setEdit(false)} title="Modifier le fournisseur" wide testid="edit-supplier-modal">
        <SupplierForm initial={s} onSaved={() => { setEdit(false); load(); }} />
      </Modal>
      <span className="hidden">{fdate("")}</span>
    </div>
  );
}
